/**
 * share_flow tool - Share a flow with users or groups
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import type { FlowOwnerModification } from "../api/types.js";
import { logger } from "../utils/logger.js";

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const shareFlowInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID)"),
  shares: z.array(z.object({
    principalId: z.string().regex(GUID_RE, "principalId must be a valid GUID").describe("The user or group ID (Microsoft Entra object ID)"),
    principalType: z.enum(["User", "Group", "ServicePrincipal"]).describe("Type of principal"),
    role: z.enum(["CanEdit", "CanView"]).describe("Permission level to grant"),
    tenantId: z.string().regex(GUID_RE, "tenantId must be a valid GUID").optional().describe("Tenant ID (optional, uses current tenant if omitted)"),
  })).describe("List of principals to share with"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
});

export type ShareFlowInput = z.infer<typeof shareFlowInputSchema>;

export const shareFlowTool = {
  name: "share_flow",
  description:
    "Share a flow with users, groups, or service principals. Grant them either 'CanEdit' (can modify the flow) or 'CanView' (run-only access) permissions. You need the Microsoft Entra object ID of the principal to share with.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID (GUID)",
      },
      shares: {
        type: "array",
        description: "List of principals to share with",
        items: {
          type: "object",
          properties: {
            principalId: {
              type: "string",
              description: "The user or group ID (Microsoft Entra object ID)",
            },
            principalType: {
              type: "string",
              enum: ["User", "Group", "ServicePrincipal"],
              description: "Type of principal",
            },
            role: {
              type: "string",
              enum: ["CanEdit", "CanView"],
              description: "Permission level to grant",
            },
            tenantId: {
              type: "string",
              description: "Tenant ID (optional, uses current tenant if omitted)",
            },
          },
          required: ["principalId", "principalType", "role"],
        },
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
    },
    required: ["flowId", "shares"],
  },
};

export async function handleShareFlow(
  api: FlowManagementApi,
  input: unknown
): Promise<string> {
  const parsed = shareFlowInputSchema.parse(input);

  logger.info({ input: parsed }, "Executing share_flow");

  const modifications: FlowOwnerModification[] = parsed.shares.map((share) => ({
    properties: {
      roleName: share.role,
      principal: {
        id: share.principalId,
        type: share.principalType,
        tenantId: share.tenantId,
      },
    },
  }));

  await api.modifyFlowOwners(parsed.flowId, modifications, parsed.environment);

  const roleText: Record<string, string> = {
    Owner: "Owner (edit, share, delete, run)",
    CanEdit: "Can edit (edit and run, cannot share)",
    CanView: "Run only",
  };
  const lines = [`Shared flow ${parsed.flowId} with ${parsed.shares.length} principal${parsed.shares.length === 1 ? "" : "s"}:`];
  for (const share of parsed.shares) {
    lines.push(`  • ${roleText[share.role] ?? share.role}: ${share.principalType} ${share.principalId}`);
  }
  lines.push("Check the result with get_flow_permissions. Sharing does not create connections for the recipient; connector actions run under the flow's own connections.");
  return lines.join("\n");
}
